import {
  Component,
  computed,
  ElementRef,
  signal,
  viewChild,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslatePipe } from "@ngx-translate/core";
import { AppState } from "src/app/ngrx/reducers";
import { InstructionDetailsSelectors } from "src/app/ngrx/selectors";
import { PagedPdfViewerComponent } from "../../common/paged-pdf-viewer/paged-pdf-viewer.component";

import { ChapterQuizzComponent } from "../chapter-quizz/chapter-quizz.component";
import { Answer } from "src/app/models/question";
import { InstructionDetailsActions } from "src/app/ngrx/actions";
import { LoadingIndicatorComponent } from "../../utils/loading-indicator/loading-indicator.component";
import {
  AppResizeEvent,
  TailwindBreakpointDirective,
} from "src/app/directives/tailwind-breakpoint.directive";
import { VideoPlayerComponent } from "../../common/video-player/video-player.component";
import { NgIconComponent, provideIcons } from "@ng-icons/core";
import { lucideDownload } from "@ng-icons/lucide";

type DialogActions = "confirm" | "cancel";

@Component({
  selector: "app-chapter-details",
  imports: [
    TranslatePipe,
    PagedPdfViewerComponent,
    ChapterQuizzComponent,
    LoadingIndicatorComponent,
    TailwindBreakpointDirective,
    VideoPlayerComponent,
    NgIconComponent,
  ],
  templateUrl: "./chapter-details.component.html",
  styles: ``,
  providers: [provideIcons({ lucideDownload })],
})
export class ChapterDetailsComponent {
  chapters = this.store.selectSignal(InstructionDetailsSelectors.chapters);
  questions = this.store.selectSignal(InstructionDetailsSelectors.questions);

  selectedChapter = this.store.selectSignal(
    InstructionDetailsSelectors.selectedChapterAndInstructionConfirmation,
  );

  instruction = this.store.selectSignal(
    InstructionDetailsSelectors.myInstruction,
  );

  downloadLink = computed(() => {
    const selectedChapter = this.selectedChapter();

    if (selectedChapter.selectedChapter) {
      return selectedChapter.selectedChapter.downloadLink;
    } else {
      return "";
    }
  });

  documentType = computed(() => {
    const selectedChapter = this.selectedChapter();
    const mimeType =
      selectedChapter.selectedChapter != undefined
        ? selectedChapter.selectedChapter.postProcessedMimeType.toLocaleLowerCase()
        : "none";

    if (mimeType.includes("video")) {
      return "video";
    } else if (mimeType.includes("pdf")) {
      return "pdf";
    } else {
      return "none";
    }
  });

  chapterTitle = computed(() => {
    const selectedChapter = this.selectedChapter();
    return selectedChapter.selectedChapter != undefined
      ? selectedChapter.selectedChapter.title
      : "";
  });

  currentChapterConfirmation = computed(() => {
    const confirmations = this.chapters().chapterConfirmations;
    const currentChapter = this.selectedChapter().selectedChapter;

    return currentChapter == undefined
      ? undefined
      : confirmations.find((c) => c.chapter == currentChapter.name);
  });

  canConfirm = computed(() => {
    const instruction = this.instruction();
    const result =
      !instruction.isLoadingMyInstruction &&
      instruction.myInstruction &&
      instruction.myInstruction.type != "Information";
    return result == true; // why is result boolean | undefined?
  });

  video = computed(() => ({
    url: this.selectedChapter().selectedChapter?.downloadLink || "",
    contentType:
      this.selectedChapter().selectedChapter?.postProcessedMimeType || "",
  }));

  isConfirmingChapter = computed(() => this.chapters().isConfirmingChapter);

  dialog =
    viewChild.required<ElementRef<HTMLDialogElement>>("confirmationDialog");

  pdfMinHeight = signal<number>(512);

  constructor(private readonly store: Store<AppState>) {}

  closeDialog() {
    this.dialog().nativeElement.close();
  }

  questionsAnswered({ answers }: { answers: Answer[] }) {
    const selectedChapter = this.selectedChapter();

    if (
      selectedChapter &&
      selectedChapter.selectedChapter &&
      selectedChapter.myInstructionConfirmation
    ) {
      this.store.dispatch(
        InstructionDetailsActions.confirmChapter({
          chapter: selectedChapter.selectedChapter,
          answers: answers,
          instructionConfirmation: selectedChapter.myInstructionConfirmation,
        }),
      );
    }
  }

  handleDocumentResized({ breakpoint }: AppResizeEvent) {
    console.log("handleDocumentResized: ", breakpoint);
    if (breakpoint == "2xl") {
      this.pdfMinHeight.set(1920);
    } else if (breakpoint == "xl") {
      this.pdfMinHeight.set(1280);
    } else if (breakpoint == "lg") {
      this.pdfMinHeight.set(1080);
    } else if (breakpoint == "md") {
      this.pdfMinHeight.set(720);
    } else {
      this.pdfMinHeight.set(720);
    }
  }

  download(
    url: string,
    chapterTitle: string,
    documentType: "pdf" | "video" | "none",
  ) {
    var extension = "";
    switch (documentType) {
      case "video":
        extension = ".mp4";
        break;
      case "pdf":
        extension = ".pdf";
        break;
      default:
        break;
    }

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = `${chapterTitle}${extension}`; // XXX: does only work when content disposition header is set!

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
